"use client";

import { Suspense, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import LocalizedClientLink from "@modules/common/components/localized-client-link";
import CartButton from "@modules/layout/components/cart-button";
import SideMenu, { NavCategory, NavMenuLink } from "@modules/layout/components/side-menu";
import StorefrontLogo from "@modules/layout/components/storefront-logo";
import SearchBar from "@modules/search/components/search-bar";
import { MagnifyingGlass, ShoppingBag } from "@medusajs/icons";
import { NavigationMenu, NavigationMenuItem } from "@lib/data/navigation";
import type { AnnouncementBarSettings } from "@lib/data/design";
import type { StorefrontConfig } from "@lib/data/store-config";
import { buildRepeatedAnnouncementMessages, computeAnnouncementDurationSeconds, computeAnnouncementRepeatCount, DEFAULT_ANNOUNCEMENT_DURATION_SECONDS } from "@lib/util/announcement-marquee";
type NavProps = {
  menu: NavigationMenu;
  storeConfig: StorefrontConfig;
  announcementBar: AnnouncementBarSettings;
};
const FALLBACK_NAV_CATEGORIES: NavCategory[] = [{
  id: "shop",
  name: "Shop ALL",
  href: "/store"
}, {
  id: "novedades",
  name: "Novedades",
  href: "/store?sort=created_at"
}, {
  id: "sale",
  name: "SALE",
  href: "/store?discount=true"
}];
const toTestId = (value: string) => value.toLowerCase().trim().replace(/\s+/g, "-").replace(/[^a-z0-9_-]/g, "");
const isExternalHref = (href?: string | null) => typeof href === "string" && /^https?:\/\//i.test(href);
const mapMenuItemToNavLink = (item: NavigationMenuItem): NavMenuLink | null => {
  const name = item.title.trim();
  if (!name) {
    return null;
  }
  const href = item.href ?? null;
  const hasChildren = item.children.length > 0;
  if (!href && !hasChildren) {
    return null;
  }
  const external = Boolean(href) && isExternalHref(href);
  return {
    id: item.itemId,
    name,
    href: href ?? undefined,
    target: external ? "_blank" : undefined,
    isExternal: external
  };
};
const mapMenuItemToCategory = (item: NavigationMenuItem): NavCategory | null => {
  const baseLink = mapMenuItemToNavLink(item);
  if (!baseLink) {
    return null;
  }
  const subcategories = item.children.map(mapMenuItemToNavLink).filter((sub): sub is NavMenuLink => Boolean(sub));
  return {
    ...baseLink,
    subcategories: subcategories.length ? subcategories : undefined
  };
};
const renderMenuLink = (item: NavMenuLink, {
  className,
  testId,
  onClick,
  onFocus,
  onBlur
}: {
  className: string;
  testId: string;
  onClick?: () => void;
  onFocus?: () => void;
  onBlur?: () => void;
}) => {
  if (!item.href) {
    return <span className={className} data-testid={testId} onFocus={onFocus} onBlur={onBlur}>
        {item.name}
      </span>;
  }
  if (item.isExternal) {
    return <a href={item.href} className={className} data-testid={testId} onClick={onClick} onFocus={onFocus} onBlur={onBlur}>
        {item.name}
      </a>;
  }
  return <LocalizedClientLink href={item.href} className={className} data-testid={testId} onClick={onClick} target={item.target} onFocus={onFocus} onBlur={onBlur}>
      {item.name}
    </LocalizedClientLink>;
};
export default function Nav({
  menu,
  storeConfig,
  announcementBar
}: NavProps) {
  const mobileActionClass = "h-10 w-10 inline-flex items-center justify-center leading-none cursor-pointer touch-manipulation text-gray-700 transition-colors active:text-black";
  const baseCategories = useMemo(() => {
    const items = menu?.items ?? [];
    const mapped = items.map(mapMenuItemToCategory).filter((category): category is NavCategory => Boolean(category));
    if (!mapped.length) {
      return FALLBACK_NAV_CATEGORIES;
    }
    return mapped;
  }, [menu]);
  const categories = useMemo(() => [...baseCategories], [baseCategories]);
  const announcementMessages = useMemo(() => (announcementBar?.messages ?? []).map(entry => typeof entry?.text === "string" ? entry.text.trim() : "").filter((entry_0): entry_0 is string => Boolean(entry_0)), [announcementBar]);
  const showAnnouncementBar = announcementBar?.enabled === true && announcementMessages.length > 0;
  const announcementMessageSignature = useMemo(() => announcementMessages.join("\u0001"), [announcementMessages]);
  const announcementViewportRef = useRef<HTMLDivElement | null>(null);
  const announcementCycleMeasureRef = useRef<HTMLDivElement | null>(null);
  const announcementSegmentMeasureRef = useRef<HTMLDivElement | null>(null);
  const [announcementRepeatCount, setAnnouncementRepeatCount] = useState(1);
  const [announcementDurationSeconds, setAnnouncementDurationSeconds] = useState(DEFAULT_ANNOUNCEMENT_DURATION_SECONDS);
  const announcementRepeatCountRef = useRef(announcementRepeatCount);
  const announcementDurationSecondsRef = useRef(announcementDurationSeconds);
  const announcementSegmentMessages = useMemo(() => buildRepeatedAnnouncementMessages(announcementMessages, announcementRepeatCount), [announcementMessages, announcementRepeatCount]);
  const announcementTrackStyle = useMemo(() => ({
    "--announcement-scroll-duration": `${announcementDurationSeconds}s`
  }) as CSSProperties, [announcementDurationSeconds]);
  const [isSearchOpen, setSearchOpen] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    announcementRepeatCountRef.current = announcementRepeatCount;
  }, [announcementRepeatCount]);
  useEffect(() => {
    announcementDurationSecondsRef.current = announcementDurationSeconds;
  }, [announcementDurationSeconds]);
  const clearCloseTimeout = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };
  const openCategory = (index: number) => {
    clearCloseTimeout();
    setHoveredIndex(index);
  };
  const scheduleClose = () => {
    clearCloseTimeout();
    closeTimeoutRef.current = setTimeout(() => {
      setHoveredIndex(null);
    }, 150);
  };
  useEffect(() => {
    return () => {
      clearCloseTimeout();
    };
  }, []);
  useEffect(() => {
    if (!showAnnouncementBar) {
      setAnnouncementRepeatCount(1);
      setAnnouncementDurationSeconds(DEFAULT_ANNOUNCEMENT_DURATION_SECONDS);
      announcementRepeatCountRef.current = 1;
      announcementDurationSecondsRef.current = DEFAULT_ANNOUNCEMENT_DURATION_SECONDS;
    }
  }, [showAnnouncementBar]);
  useEffect(() => {
    if (!showAnnouncementBar) {
      return;
    }
    const viewportElement = announcementViewportRef.current;
    const cycleMeasureElement = announcementCycleMeasureRef.current;
    const segmentMeasureElement = announcementSegmentMeasureRef.current;
    if (!viewportElement || !cycleMeasureElement || !segmentMeasureElement) {
      return;
    }
    let frameId: number | null = null;
    const scheduleMeasurements = () => {
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }
      frameId = window.requestAnimationFrame(() => {
        frameId = null;
        updateMeasurements();
      });
    };
    const updateMeasurements = () => {
      const nextRepeatCount = computeAnnouncementRepeatCount(viewportElement.clientWidth, cycleMeasureElement.scrollWidth);
      if (announcementRepeatCountRef.current !== nextRepeatCount) {
        announcementRepeatCountRef.current = nextRepeatCount;
        setAnnouncementRepeatCount(nextRepeatCount);
        scheduleMeasurements();
        return;
      }
      const nextDuration = computeAnnouncementDurationSeconds(segmentMeasureElement.scrollWidth);
      if (Math.abs(announcementDurationSecondsRef.current - nextDuration) < 0.1) {
        return;
      }
      announcementDurationSecondsRef.current = nextDuration;
      setAnnouncementDurationSeconds(nextDuration);
    };
    scheduleMeasurements();
    let resizeObserver: ResizeObserver | undefined;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => {
        scheduleMeasurements();
      });
      resizeObserver.observe(viewportElement);
      resizeObserver.observe(cycleMeasureElement);
      resizeObserver.observe(segmentMeasureElement);
    }
    window.addEventListener("resize", scheduleMeasurements);
    return () => {
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }
      resizeObserver?.disconnect();
      window.removeEventListener("resize", scheduleMeasurements);
    };
  }, [announcementMessageSignature, showAnnouncementBar]);
  return <div className="relative">
      <SearchBar isOpen={isSearchOpen} onClose={() => setSearchOpen(false)} />
      <div className="sticky top-0 inset-x-0 z-50 bg-white">
        {showAnnouncementBar ? <div className="announcement-bar" role="status" aria-live="polite">
            <div className="announcement-bar__viewport" ref={announcementViewportRef}>
              <div className="announcement-bar__track" style={announcementTrackStyle}>
                <div className="announcement-bar__segment">
                  {announcementSegmentMessages.map((message, index_0) => <span key={`segment-a-${index_0}-${message}`} className="announcement-bar__item">
                      {message}
                    </span>)}
                </div>
                <div className="announcement-bar__segment is-duplicate" aria-hidden>
                  {announcementSegmentMessages.map((message_0, index_1) => <span key={`segment-b-${index_1}-${message_0}`} className="announcement-bar__item">
                      {message_0}
                    </span>)}
                </div>
              </div>

              <div className="announcement-bar__segment announcement-bar__measure" ref={announcementCycleMeasureRef} aria-hidden>
                {announcementMessages.map((message_1, index_2) => <span key={`cycle-measure-${index_2}-${message_1}`} className="announcement-bar__item">
                    {message_1}
                  </span>)}
              </div>
              <div className="announcement-bar__segment announcement-bar__measure" ref={announcementSegmentMeasureRef} aria-hidden>
                {announcementSegmentMessages.map((message_2, index_3) => <span key={`segment-measure-${index_3}-${message_2}`} className="announcement-bar__item">
                    {message_2}
                  </span>)}
              </div>
            </div>
          </div> : null}
        <div className="border-b border-gray-100">
          <header className="mx-auto py-2 md:py-4">
            <div className="content-container flex items-center gap-2 md:gap-6">
              <div className="flex shrink-0 items-center">
                <LocalizedClientLink href="/" className="txt-compact-xlarge-plus hover:text-ui-fg-base uppercase" data-testid="nav-store-link">
                  <StorefrontLogo brandName={storeConfig?.brandName || ""} logoUrl={storeConfig?.logoUrl || ""} width={96} height={32} className="h-auto w-[64px] md:w-[96px]" textClassName="whitespace-nowrap" />
                </LocalizedClientLink>
              </div>

              {/* Desktop search pill */}
              <button type="button" onClick={() => setSearchOpen(true)} className="hidden md:flex  min-w-0 flex-1 items-center gap-2 rounded-sm h-12 border border-gray-200 px-4 text-gray-400 transition hover:border-gray-500 hover:text-gray-600" data-testid="nav-search-button" aria-label="Abrir búsqueda">
                <MagnifyingGlass className="h-4 w-4 shrink-0" />
                <span className="text-sm font-lealti-light">Buscar</span>
              </button>

              {/* Mobile: push icons to the right */}
              <div className="flex-1 md:hidden" />

              <div className="flex shrink-0 items-center gap-4 md:gap-3">
                {/* Mobile search icon – always first */}
                <button type="button" onClick={() => setSearchOpen(true)} className={`${mobileActionClass} md:hidden`} aria-label="Buscar">
                  <MagnifyingGlass className="h-5 w-5" />
                </button>

                {/* Cart: mobile order 1, desktop order 3 */}
                <div className="md:order-3">
                  <Suspense fallback={<LocalizedClientLink className={`${mobileActionClass} md:h-auto md:w-auto md:leading-normal`} href="/checkout" data-testid="nav-cart-link">
                        <ShoppingBag className="h-5 w-5" />
                      </LocalizedClientLink>}>
                    <CartButton />
                  </Suspense>
                </div>

                {/* Menu toggler: mobile only, always last */}
                <div className="md:hidden">
                  <SideMenu categories={baseCategories} />
                </div>
              </div>
            </div>
          </header>

          <nav className="hidden md:block">
            <div className="content-container">
              <div className="hidden md:flex h-12 items-center gap-8">
                {categories.map((category_0, idx) => <div key={category_0.id} className="relative" onMouseEnter={() => openCategory(idx)} onMouseLeave={scheduleClose}>
                    {renderMenuLink(category_0, {
                  className: "text-sm font-lealti-light tracking-wide text-gray-700 uppercase transition-colors hover:text-black",
                  testId: `nav-${toTestId(category_0.name)}-link`,
                  onClick: () => setHoveredIndex(null),
                  onFocus: () => openCategory(idx),
                  onBlur: scheduleClose
                })}

                    {category_0.subcategories ? <div className={`absolute left-0 top-full mt-2 ${hoveredIndex === idx ? "block" : "hidden"} min-w-[220px] rounded-md border border-gray-200 bg-white p-2 shadow-lg`} onMouseEnter={() => openCategory(idx)} onMouseLeave={scheduleClose}>
                        <ul className="flex flex-col gap-1">
                          {category_0.subcategories.map(sub => <li key={sub.id}>
                              {renderMenuLink(sub, {
                        className: "block rounded px-2 py-2 text-sm text-gray-700 transition hover:bg-gray-50 hover:text-black",
                        testId: `nav-sub-${toTestId(sub.name)}-link`,
                        onClick: () => setHoveredIndex(null),
                        onFocus: () => openCategory(idx),
                        onBlur: scheduleClose
                      })}
                            </li>)}
                        </ul>
                      </div> : null}
                  </div>)}
              </div>
            </div>
          </nav>
        </div>
      </div>
    </div>;
}